/**
 * CAScopeSection Component
 * V120: Construction Administration scope definition for proposals.
 *
 * Allows defining basic vs monthly site reviews, review fees,
 * and close-out document requirements.
 */
import * as React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';

export interface CAScopeConfig {
  review_type: 'basic' | 'monthly';
  review_count: number;
  review_fee_pct: number;
  additional_review_fee: number;
  close_out_docs: string[];
  milestones?: string[];
  frequency?: string;
}

interface CAScopeSectionProps {
  projectId: string;
  caScope: CAScopeConfig | null;
  onUpdate: (scope: CAScopeConfig) => void;
  isSaving?: boolean;
}

const DEFAULT_MILESTONES = ['Framing Review', 'Final Review'];
const DEFAULT_CLOSE_OUT_DOCS = ['Schedule C-B', 'Schedule C-A', 'Engineer Schedules'];

function buildDefault(): CAScopeConfig {
  return {
    review_type: 'basic',
    review_count: 2,
    review_fee_pct: 50,
    additional_review_fee: 500,
    close_out_docs: [...DEFAULT_CLOSE_OUT_DOCS],
    milestones: [...DEFAULT_MILESTONES],
    frequency: '',
  };
}

export function CAScopeSection({ projectId: _projectId, caScope, onUpdate, isSaving }: CAScopeSectionProps) {
  const [local, setLocal] = useState<CAScopeConfig>(() => caScope ?? buildDefault());
  const [newDoc, setNewDoc] = useState('');
  const [newMilestone, setNewMilestone] = useState('');

  // Sync if parent prop changes (e.g. after save)
  React.useEffect(() => {
    if (caScope) {
      setLocal(caScope);
    }
  }, [caScope]);

  const update = (patch: Partial<CAScopeConfig>) => {
    setLocal(prev => ({ ...prev, ...patch }));
  };

  const handleSave = () => {
    onUpdate(local);
  };

  const addDoc = () => {
    const trimmed = newDoc.trim();
    if (!trimmed) return;
    update({ close_out_docs: [...local.close_out_docs, trimmed] });
    setNewDoc('');
  };

  const removeDoc = (idx: number) => {
    update({ close_out_docs: local.close_out_docs.filter((_, i) => i !== idx) });
  };

  const addMilestone = () => {
    const trimmed = newMilestone.trim();
    if (!trimmed) return;
    update({ milestones: [...(local.milestones ?? []), trimmed] });
    setNewMilestone('');
  };

  const removeMilestone = (idx: number) => {
    update({ milestones: (local.milestones ?? []).filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-5">
      {/* Review Type Toggle */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">Review Type</Label>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="review_type"
              value="basic"
              checked={local.review_type === 'basic'}
              onChange={() => update({ review_type: 'basic' })}
              className="text-blue-600"
            />
            <span className="text-sm">Basic Reviews (per milestone)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="review_type"
              value="monthly"
              checked={local.review_type === 'monthly'}
              onChange={() => update({ review_type: 'monthly' })}
              className="text-blue-600"
            />
            <span className="text-sm">Monthly Reviews</span>
          </label>
        </div>
      </div>

      {/* Basic-specific fields */}
      {local.review_type === 'basic' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ca-review-count" className="text-sm font-medium text-gray-700">
                Review Count
              </Label>
              <Input
                id="ca-review-count"
                type="number"
                min={1}
                value={local.review_count}
                onChange={(e) => update({ review_count: parseInt(e.target.value) || 1 })}
                className="mt-1"
              />
            </div>
          </div>

          {/* Milestones */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Milestones</Label>
            <div className="space-y-2">
              {(local.milestones ?? []).map((m, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={m}
                    onChange={(e) => {
                      const updated = [...(local.milestones ?? [])];
                      updated[i] = e.target.value;
                      update({ milestones: updated });
                    }}
                    className="text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeMilestone(i)}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Add milestone..."
                  value={newMilestone}
                  onChange={(e) => setNewMilestone(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMilestone(); } }}
                  className="text-sm"
                />
                <Button variant="outline" size="sm" onClick={addMilestone} className="h-8 px-2">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Monthly-specific fields */}
      {local.review_type === 'monthly' && (
        <div>
          <Label htmlFor="ca-frequency" className="text-sm font-medium text-gray-700">
            Frequency / Schedule
          </Label>
          <Input
            id="ca-frequency"
            placeholder="e.g. First Tuesday of each month"
            value={local.frequency ?? ''}
            onChange={(e) => update({ frequency: e.target.value })}
            className="mt-1"
          />
        </div>
      )}

      {/* Fee Settings */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ca-fee-pct" className="text-sm font-medium text-gray-700">
            Review Fee (% of CA fee)
          </Label>
          <div className="relative mt-1">
            <Input
              id="ca-fee-pct"
              type="number"
              min={0}
              max={100}
              value={local.review_fee_pct}
              onChange={(e) => update({ review_fee_pct: parseFloat(e.target.value) || 0 })}
              className="pr-8"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
          </div>
        </div>
        <div>
          <Label htmlFor="ca-additional-fee" className="text-sm font-medium text-gray-700">
            Additional Review Fee (per review beyond scope)
          </Label>
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <Input
              id="ca-additional-fee"
              type="number"
              min={0}
              value={local.additional_review_fee}
              onChange={(e) => update({ additional_review_fee: parseFloat(e.target.value) || 0 })}
              className="pl-7"
            />
          </div>
        </div>
      </div>

      {/* Close-out Documents */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">Close-out Documents Required</Label>
        <div className="space-y-2">
          {local.close_out_docs.map((doc, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={doc}
                onChange={(e) => {
                  const updated = [...local.close_out_docs];
                  updated[i] = e.target.value;
                  update({ close_out_docs: updated });
                }}
                className="text-sm"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeDoc(i)}
                className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Add document..."
              value={newDoc}
              onChange={(e) => setNewDoc(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDoc(); } }}
              className="text-sm"
            />
            <Button variant="outline" size="sm" onClick={addDoc} className="h-8 px-2">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="pt-2">
        <Button onClick={handleSave} disabled={isSaving} size="sm">
          {isSaving ? 'Saving...' : 'Save CA Scope'}
        </Button>
      </div>
    </div>
  );
}
